import {
  Activity,
  Building2,
  Cable,
  FileText,
  Gauge,
  Headphones,
  LayoutDashboard,
  Lock,
  Network,
  Settings,
  Shield,
  Users,
  Menu,
} from "lucide-react";
import { brand } from "../config/brand";
import { dashboardNav, utilityNav, type NavItem } from "../config/navigation";

function Icon({ name }: { name: NavItem["icon"] }) {
  const props = { size: 16, strokeWidth: 1.8 } as const;
  switch (name) {
    case "overview":
      return <LayoutDashboard {...props} />;
    case "tenants":
      return <Building2 {...props} />;
    case "network":
      return <Network {...props} />;
    case "leaders":
      return <Activity {...props} />;
    case "followers":
      return <Users {...props} />;
    case "risk":
      return <Shield {...props} />;
    case "bridge":
      return <Cable {...props} />;
    case "audit":
      return <FileText {...props} />;
    case "support":
      return <Headphones {...props} />;
    case "settings":
      return <Settings {...props} />;
    case "status":
      return <Gauge {...props} />;
    case "login":
      return <Lock {...props} />;
  }
}

function NavLinks({
  items,
  pathname,
  onNavigate,
}: {
  items: NavItem[];
  pathname: string;
  onNavigate: (path: string) => void;
}) {
  return (
    <nav className="sidebar-nav" aria-label="Primary">
      {items.map((item) => {
        const active =
          pathname === item.path ||
          (item.path === "/dashboard/overview" && pathname === "/dashboard");
        return (
          <a
            key={item.path}
            href={item.path}
            className={`nav-link${active ? " active" : ""}`}
            aria-current={active ? "page" : undefined}
            onClick={(e) => {
              e.preventDefault();
              onNavigate(item.path);
            }}
          >
            <Icon name={item.icon} />
            <span>{item.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

export function Sidebar({
  pathname,
  open,
  onNavigate,
  onLogout,
  sessionEmail,
}: {
  pathname: string;
  open: boolean;
  onNavigate: (path: string) => void;
  onLogout?: () => void;
  sessionEmail?: string;
}) {
  return (
    <aside className={`sidebar${open ? " open" : ""}`} aria-label="Sidebar">
      <div className="sidebar-brand">
        <div className="sidebar-mark" aria-hidden="true">
          {brand.logoText}
        </div>
        <div className="sidebar-brand-text">
          <strong>{brand.productName}</strong>
          <span>{brand.environmentLabel}</span>
        </div>
      </div>
      <NavLinks items={dashboardNav} pathname={pathname} onNavigate={onNavigate} />
      <div style={{ marginTop: 8 }}>
        <NavLinks items={utilityNav} pathname={pathname} onNavigate={onNavigate} />
      </div>
      <div className="sidebar-footer">
        {sessionEmail ? <div style={{ marginBottom: 8 }}>{sessionEmail}</div> : null}
        {onLogout ? (
          <button type="button" className="btn btn-ghost" onClick={onLogout} style={{ width: "100%" }}>
            Sign out
          </button>
        ) : null}
        <div style={{ marginTop: 8 }}>Live execution remains disabled.</div>
      </div>
    </aside>
  );
}

export function Topbar({
  title,
  subtitle,
  onMenu,
}: {
  title: string;
  subtitle: string;
  onMenu: () => void;
}) {
  return (
    <header className="topbar">
      <div className="topbar-left">
        <button type="button" className="menu-toggle" onClick={onMenu} aria-label="Open navigation">
          <Menu size={16} />
        </button>
        <div>
          <div className="topbar-title">{title}</div>
          <div className="topbar-subtitle">{subtitle}</div>
        </div>
      </div>
      <div className="topbar-right">
        <span className="status-pill ok">Simulation</span>
        <span className="status-pill locked">Live off</span>
      </div>
    </header>
  );
}
